import { LegalShell, CompanyBlock, type LegalLang } from './legalShared'
import { useCompany } from '../hooks/useCompanyInfo'

const APP = 'Provisyer'

// Mesafeli Satış Sözleşmesi — the distance sales agreement the buyer approves
// on the cart page before payment. Turkish consumer law (6502 sayılı TKHK +
// Mesafeli Sözleşmeler Yönetmeliği) requires it, and acquiring banks check for
// it during site review.
//
// NOTE: this is a good-faith template built around our actual product and
// process. Have counsel review it before it carries real transactions.
export default function DistanceSalesPage({ lang = 'tr' }: { lang?: LegalLang }) {
  return lang === 'en' ? <DistanceEN /> : <DistanceTR />
}

function DistanceTR() {
  const company = useCompany()
  return (
    <LegalShell
      lang="tr"
      page="distance"
      title="Mesafeli Satış Sözleşmesi"
      updated="Son güncelleme: 13 Ağustos 2026"
    >
      <h2>1. Taraflar</h2>
      <h3>1.1. Satıcı</h3>
      <CompanyBlock lang="tr" company={company} />
      <h3>1.2. Alıcı</h3>
      <p>
        {APP} platformunda hesap oluşturan ve sipariş veren gerçek veya tüzel kişidir. Alıcı&apos;nın ad, soyad/unvan,
        e-posta adresi ve fatura bilgileri sipariş sırasında verdiği ve hesabında kayıtlı olan bilgilerdir.
      </p>

      <h2>2. Sözleşmenin Konusu</h2>
      <p>
        İşbu sözleşmenin konusu, Alıcı&apos;nın {APP} platformu üzerinden elektronik ortamda siparişini verdiği,
        aşağıda nitelikleri ve satış fiyatı belirtilen dijital hizmetlerin sunulması ile ilgili olarak 6502 sayılı
        Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri uyarınca tarafların hak ve
        yükümlülüklerinin belirlenmesidir.
      </p>
      <p>
        Alıcı, satışa konu hizmetin temel nitelikleri, satış fiyatı, ödeme şekli, ifa koşulları ve Satıcı&apos;nın tam
        ticari unvanı, açık adresi ve iletişim bilgileri konusunda ön bilgilendirmenin sipariş öncesinde kendisine
        yapıldığını, sepet sayfasında bu bilgileri gördüğünü ve elektronik ortamda onay verdiğini kabul eder.
      </p>

      <h2>3. Sözleşme Konusu Hizmet ve Ödeme Bilgileri</h2>
      <p>
        Sözleşmeye konu hizmetin türü, adedi, abonelik dönemi (aylık veya yıllık) ve vergiler dâhil toplam satış
        bedeli, sipariş sırasında sepet sayfasında ve sipariş onayında açıkça gösterilir. Güncel ürün ve fiyat listesi
        <a href="/urunler"> Ürünler</a> sayfasında yer alır.
      </p>
      <ul>
        <li>Tüm fiyatlar Türk Lirası (TL) cinsindendir ve tahsilat TL üzerinden yapılır.</li>
        <li>Ürün sayfalarında gösterilen fiyatlara KDV dâhil değildir; KDV sepet sayfasında ayrıca hesaplanır ve toplama eklenir.</li>
        <li>
          Ödeme, banka veya kredi kartı ile 3D Secure doğrulaması kullanılarak, ödeme kuruluşunun güvenli ödeme
          sayfası üzerinden yapılır. Kart bilgileri Satıcı tarafından görülmez ve saklanmaz.
        </li>
        <li>Abonelikler, Alıcı iptal edene kadar seçilen dönem sonunda otomatik olarak yenilenir ve aynı tutar tahsil edilir.</li>
      </ul>

      <h2>4. Genel Hükümler</h2>
      <ul>
        <li>
          Alıcı, sözleşme konusu hizmetin temel niteliklerini, satış fiyatını ve ödeme şeklini okuyup bilgi sahibi
          olduğunu ve elektronik ortamda gerekli teyidi verdiğini beyan eder.
        </li>
        <li>
          Sözleşme konusu hizmet, Alıcı&apos;nın hesabına tanımlanmak suretiyle sunulur; fiziksel bir teslimat söz
          konusu değildir.
        </li>
        <li>
          Alıcı, hizmeti hukuka aykırı amaçlarla kullanamaz. Kullanım kurallarına
          <a href="/hizmet-sartlari"> Hizmet Şartları</a> sayfasından ulaşılabilir.
        </li>
        <li>
          Satıcı, mücbir sebepler veya kendisinden kaynaklanmayan altyapı kesintileri nedeniyle hizmeti geçici olarak
          sunamazsa durumu Alıcı&apos;ya bildirir.
        </li>
      </ul>

      <h2>5. İfa ve Hizmetin Sunulması</h2>
      <p>
        Sözleşme konusu hizmet dijital niteliktedir ve ödemenin onaylanmasının ardından <strong>derhâl</strong>,
        teknik bir aksaklık hâlinde en geç <strong>24 saat içinde</strong> Alıcı&apos;nın hesabında kullanıma açılır.
        Alıcı, hizmete {APP} paneli üzerinden erişir; kurulum tamamlandığında hesabında bildirim görüntülenir.
      </p>
      <p>
        Ödemenin ilgili banka veya ödeme kuruluşu tarafından onaylanmaması hâlinde Satıcı&apos;nın hizmeti sunma
        yükümlülüğü doğmaz.
      </p>

      <h2>6. Cayma Hakkı</h2>
      <p>
        Alıcı, mesafeli sözleşmelerde kural olarak sözleşmenin kurulduğu tarihten itibaren <strong>14 (on dört) gün</strong>{' '}
        içinde hiçbir gerekçe göstermeksizin ve cezai şart ödemeksizin cayma hakkına sahiptir.
      </p>
      <div className="legal-warning">
        <strong>Önemli istisna:</strong> Mesafeli Sözleşmeler Yönetmeliği&apos;nin 15/1-(ğ) maddesi uyarınca, elektronik
        ortamda anında ifa edilen hizmetler ile tüketiciye anında teslim edilen gayrimaddi mallara ilişkin
        sözleşmelerde cayma hakkı kullanılamaz. Hizmetimiz ödeme onayının ardından anında ifa edildiğinden, Alıcı
        sipariş sırasında bu istisnayı ve ifaya derhâl başlanmasını onaylamış sayılır.
      </div>
      <p>
        Buna rağmen, hizmetin hiç kullanılmamış olması hâlinde iyi niyet kuralı gereği uyguladığımız iade koşulları
        için <a href="/teslimat-ve-iade">Teslimat ve İade</a> sayfasına bakınız. Cayma bildirimi, Satıcı&apos;nın
        yukarıda belirtilen e-posta adresine iletilebilir.
      </p>

      <h2>7. İade ve İptal</h2>
      <p>
        Aboneliğin iptali, panel üzerindeki <em>Faturalandırma</em> ekranından tek tıkla yapılabilir. İptal, içinde
        bulunulan dönemin sonunda geçerli olur; dönem sonuna kadar hizmet kullanılmaya devam edilir ve sonrasında
        yenileme tahsilatı yapılmaz. Onaylanan iadeler, ödemenin yapıldığı karta, iadenin onaylanmasından itibaren
        <strong> 14 gün</strong> içinde yapılır. Bankanın karta yansıtma süresi bu süreye dâhil değildir.
      </p>

      <h2>8. Temerrüt ve Hukuki Sonuçlar</h2>
      <p>
        Alıcı&apos;nın kredi kartı ile yaptığı ödemelerde temerrüde düşmesi hâlinde, kart sahibi banka ile arasındaki
        kredi kartı sözleşmesi çerçevesinde faiz ödeyeceğini ve bankaya karşı sorumlu olacağını kabul eder. Yenileme
        tahsilatının başarısız olması hâlinde Satıcı, Alıcı&apos;yı bilgilendirir ve makul bir süre sonunda hizmeti
        askıya alabilir.
      </p>

      <h2>9. Uyuşmazlıkların Çözümü</h2>
      <p>
        İşbu sözleşmeden doğabilecek uyuşmazlıklarda, Ticaret Bakanlığı&apos;nca her yıl ilan edilen parasal sınırlar
        dâhilinde Alıcı&apos;nın veya Satıcı&apos;nın yerleşim yerindeki Tüketici Hakem Heyetleri, bu sınırların
        üzerindeki uyuşmazlıklarda ise Tüketici Mahkemeleri yetkilidir.
      </p>

      <h2>10. Yürürlük</h2>
      <p>
        Alıcı, sipariş sırasında bu sözleşmenin tüm koşullarını okuduğunu ve kabul ettiğini elektronik ortamda onay
        vererek beyan eder. Sözleşme, siparişin Satıcı tarafından onaylanması ile kurulmuş sayılır ve taraflarca
        elektronik ortamda saklanır.
      </p>
    </LegalShell>
  )
}

function DistanceEN() {
  const company = useCompany()
  return (
    <LegalShell
      lang="en"
      page="distance"
      title="Distance Sales Agreement"
      updated="Last updated: 13 August 2026"
    >
      <p>
        This agreement is governed by Turkish consumer law (Law No. 6502 and the Distance Contracts Regulation). The
        Turkish version is the binding text; this translation is provided for convenience.
      </p>

      <h2>1. Parties</h2>
      <h3>1.1. Seller</h3>
      <CompanyBlock lang="en" company={company} />
      <h3>1.2. Buyer</h3>
      <p>
        The natural or legal person who creates an account on {APP} and places an order, identified by the details
        registered on their account at the time of purchase.
      </p>

      <h2>2. Subject</h2>
      <p>
        This agreement sets out the rights and obligations of the parties in relation to the digital services ordered
        electronically by the Buyer through the {APP} platform, whose characteristics and price are stated below.
      </p>

      <h2>3. Service and Payment Details</h2>
      <p>
        The type, quantity, billing cycle (monthly or yearly) and total price including taxes are displayed on the
        cart page and in the order confirmation. The current catalogue is on the{' '}
        <a href="/products">Products</a> page.
      </p>
      <ul>
        <li>All prices are in Turkish Lira (TRY) and charged in TRY.</li>
        <li>Prices shown on product pages exclude VAT; VAT is calculated and added on the cart page.</li>
        <li>
          Payment is made by debit or credit card with 3D Secure authentication on the payment provider&apos;s secure
          page. Card details are never seen or stored by the Seller.
        </li>
        <li>Subscriptions renew automatically at the end of each cycle for the same amount until cancelled.</li>
      </ul>

      <h2>4. Performance</h2>
      <p>
        The service is digital and is activated on the Buyer&apos;s account <strong>immediately</strong> after payment
        is confirmed, and in any event within <strong>24 hours</strong> should a technical fault occur. There is no
        physical delivery.
      </p>

      <h2>5. Right of Withdrawal</h2>
      <p>
        As a rule the Buyer may withdraw within <strong>14 days</strong> of the contract being concluded without giving
        a reason.
      </p>
      <div className="legal-warning">
        <strong>Exception:</strong> under Article 15/1(ğ) of the Distance Contracts Regulation, the right of withdrawal
        does not apply to services performed instantly in electronic form or to intangible goods delivered instantly.
        Because our service is performed immediately upon payment, the Buyer approves this exception and consents to
        immediate performance when placing the order.
      </div>
      <p>
        Our good-faith refund practice for unused services is described on{' '}
        <a href="/delivery-and-returns">Delivery and Returns</a>.
      </p>

      <h2>6. Cancellation and Refunds</h2>
      <p>
        Subscriptions can be cancelled with one click from the <em>Billing</em> screen. Cancellation takes effect at
        the end of the current period; no further renewal is charged. Approved refunds are returned to the card used
        for payment within <strong>14 days</strong> of approval, excluding the bank&apos;s own settlement time.
      </p>

      <h2>7. Disputes</h2>
      <p>
        Consumer Arbitration Committees and Consumer Courts at the Buyer&apos;s or the Seller&apos;s place of
        residence have jurisdiction, within the monetary thresholds published annually by the Turkish Ministry of
        Trade.
      </p>

      <h2>8. Entry into Force</h2>
      <p>
        By approving this agreement electronically at checkout, the Buyer confirms having read and accepted all of its
        terms. The agreement is concluded upon the Seller&apos;s confirmation of the order and is retained
        electronically.
      </p>
    </LegalShell>
  )
}
